import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { Entry } from '../../services/api/timeEntry';
import { useTopScrollbarSync } from '../../hooks/useTopScrollbarSync';
import { formatWeekdayLong } from '../../utils/dateFormat';
import { formatDisplayDate } from '../../utils/format';
import { LEGACY_CZ_NUMBER_LOCALE, compareText } from '../../utils/localeFormat';

interface TimeEntryListProps {
  entries: Entry[];
  selectableEntries: Entry[];
  selectedEntryIds: Set<number>;
  onToggleEntry: (entryId: number) => void;
  onToggleAll: () => void;
  /**
   * MKS-192: toggle every selectable entry of one project group at once. The
   * per-project checkbox only renders when a parent passes this handler, so
   * screens without bulk approval are untouched.
   */
  onToggleProject?: (entryIds: number[]) => void;
  onEditEntry?: (entry: Entry) => void;
  readOnly?: boolean;
  groupingMode?: GroupingMode;
}

export type GroupingMode = 'project' | 'day';

interface ProjectGroup {
  projectId: number;
  projectName: string;
  projectFullName: string;
  entries: Entry[];
  totalHours: number;
}

interface DayGroup {
  date: string;
  displayDate: string;
  entries: Entry[];
  totalHours: number;
}

function formatDate(dateString: string): string {
  // MKS-351 — dd.mm.yyyy from the shared util.
  return formatDisplayDate(dateString);
}

function getStatusIcon(state: string, rejectionReason?: string | null) {
  switch (state) {
    case 'approved':
      return <CheckCircleIcon className="h-4 w-4 text-green-600" title="Approved" />;
    case 'submitted':
      return <ClockIcon className="h-4 w-4 text-yellow-600" title="Waiting for review" />;
    case 'rejected':
      return <XCircleIcon className="h-4 w-4 text-red-600" title={rejectionReason ? `Rejected: ${rejectionReason}` : 'Rejected'} />;
    default:
      return null;
  }
}

/**
 * MKS-192: tri-state "select this project" checkbox for a project group header.
 * `indeterminate` has no JSX attribute, so it is written onto the DOM node.
 */
function ProjectSelectCheckbox({
  checked,
  indeterminate,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  label: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      title={label}
      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
}

export function TimeEntryList({
  entries,
  selectableEntries,
  selectedEntryIds,
  onToggleEntry,
  onToggleAll,
  onToggleProject,
  onEditEntry,
  readOnly = false,
  groupingMode = 'project',
}: TimeEntryListProps) {
  // Refs for syncing scrollbars (F21-006: extracted to useTopScrollbarSync)
  const { topScrollRef, bottomScrollRef, topScrollContentRef, hasOverflow } =
    useTopScrollbarSync([entries, groupingMode]);

  // State for collapsed projects
  const [collapsedProjects, setCollapsedProjects] = useState<Set<number>>(new Set());

  // State for collapsed days (used in by-day grouping mode)
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  // Group entries by project
  const projectGroups = useMemo(() => {
    const groups = new Map<number, ProjectGroup>();

    for (const entry of entries) {
      if (!groups.has(entry.projectId)) {
        groups.set(entry.projectId, {
          projectId: entry.projectId,
          projectName: entry.project.name,
          projectFullName: entry.project.fullName || entry.project.name,
          entries: [],
          totalHours: 0,
        });
      }
      const group = groups.get(entry.projectId)!;
      group.entries.push(entry);
      group.totalHours += entry.hours;
    }

    // Sort entries within each group by date ascending (oldest first)
    for (const group of groups.values()) {
      group.entries.sort((a, b) => a.date.localeCompare(b.date));
    }

    // Convert to array and sort by project name
    return Array.from(groups.values()).sort((a, b) =>
      compareText(a.projectFullName, b.projectFullName, LEGACY_CZ_NUMBER_LOCALE)
    );
  }, [entries]);

  // Group entries by day
  const dayGroups = useMemo(() => {
    const groups = new Map<string, DayGroup>();

    for (const entry of entries) {
      if (!groups.has(entry.date)) {
        // MKS-351 — "Monday 20.11.2026": English weekday + the shared date format.
        const displayDate = `${formatWeekdayLong(entry.date)} ${formatDisplayDate(entry.date)}`;
        groups.set(entry.date, {
          date: entry.date,
          displayDate,
          entries: [],
          totalHours: 0,
        });
      }
      const group = groups.get(entry.date)!;
      group.entries.push(entry);
      group.totalHours += entry.hours;
    }

    // Sort entries within each group by project name ascending
    for (const group of groups.values()) {
      group.entries.sort((a, b) =>
        compareText(
          a.project.fullName || a.project.name,
          b.project.fullName || b.project.name,
          LEGACY_CZ_NUMBER_LOCALE
        )
      );
    }

    // Sort groups by date descending (most recent first)
    return Array.from(groups.values()).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, [entries]);

  // Check if entry is selectable (state === 'submitted')
  const selectableIds = useMemo(
    () => new Set(selectableEntries.map((e) => e.id)),
    [selectableEntries]
  );
  const isSelectable = (entry: Entry) => selectableIds.has(entry.id);

  // MKS-192: per-project bulk select. Only renders when the parent screen
  // wires a handler (the monthly approval detail).
  const showProjectSelect = !readOnly && !!onToggleProject;

  // Check if all selectable entries are selected
  const allSelected = selectableEntries.length > 0 && selectableEntries.every((e) => selectedEntryIds.has(e.id));

  // Toggle project collapse state
  const toggleProjectCollapse = (projectId: number) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Toggle day collapse state
  const toggleDayCollapse = (date: string) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">No entries reported for this period</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table-scroll-container {
          overflow-x: scroll !important;
          width: 100%;
          display: block;
        }
        .table-scroll-container::-webkit-scrollbar {
          height: 20px;
          background: #F3F4F6;
        }
        .table-scroll-container::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-top: 1px solid #E5E7EB;
        }
        .table-scroll-container::-webkit-scrollbar-thumb {
          background: #9CA3AF;
          border-radius: 10px;
          border: 4px solid #F3F4F6;
        }
        .table-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #6B7280;
        }
        .top-scroll-container {
          overflow-x: scroll !important;
          overflow-y: hidden;
          width: 100%;
          display: block;
        }
        .top-scroll-container::-webkit-scrollbar {
          height: 20px;
          background: #F3F4F6;
        }
        .top-scroll-container::-webkit-scrollbar-track {
          background: #F3F4F6;
        }
        .top-scroll-container::-webkit-scrollbar-thumb {
          background: #9CA3AF;
          border-radius: 10px;
          border: 4px solid #F3F4F6;
        }
        .top-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #6B7280;
        }
      `}</style>

      {hasOverflow && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-700 font-medium">
          ⟵ Posuňte tabulku doleva/doprava pro zobrazení všech sloupců ⟶
        </div>
      )}

      {/* Top scrollbar - only show when overflowing */}
      {hasOverflow && (
        <div ref={topScrollRef} className="top-scroll-container border-b border-gray-200">
          <div ref={topScrollContentRef} style={{ height: '1px' }}></div>
        </div>
      )}

      {/* Bottom scrollbar with table */}
      <div ref={bottomScrollRef} className={`table-scroll-container ${!hasOverflow ? 'rounded-t-xl' : ''} rounded-b-xl`}>
        <table className="w-full" style={{ minWidth: '1150px' }}>
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {!readOnly && (
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => onToggleAll()}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Hours
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comment
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rejection Reason
              </th>
              {!readOnly && onEditEntry && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white">
            {groupingMode === 'day' ? (
              dayGroups.map((group) => {
                const isCollapsed = collapsedDays.has(group.date);

                return (
                  <React.Fragment key={group.date}>
                    {/* Day Header Row */}
                    <tr className="bg-gray-100 border-t border-b border-gray-200">
                      <td colSpan={readOnly ? 6 : (onEditEntry ? 8 : 7)} className="px-4 py-3">
                        <button
                          onClick={() => toggleDayCollapse(group.date)}
                          className="flex items-center gap-2 w-full text-left hover:text-blue-600 transition-colors"
                        >
                          {isCollapsed ? (
                            <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm text-gray-900">
                            {group.displayDate}
                          </span>
                          <span className="text-xs text-gray-500 ml-auto">
                            ({group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}, {group.totalHours.toFixed(1)}h total)
                          </span>
                        </button>
                      </td>
                    </tr>

                    {/* Entry Rows */}
                    {!isCollapsed && group.entries.map((entry) => {
                      const selectable = isSelectable(entry);
                      const selected = selectedEntryIds.has(entry.id);

                      return (
                        <tr
                          key={entry.id}
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                            selected ? 'bg-blue-50' : ''
                          }`}
                        >
                          {!readOnly && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selected}
                                disabled={!selectable}
                                onChange={() => onToggleEntry(entry.id)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </td>
                          )}
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-900">
                              {entry.project.fullName || entry.project.name}
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            {/* Date is in the group header */}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="text-sm font-medium text-gray-900">
                                {entry.hours.toFixed(1)}
                              </span>
                              {getStatusIcon(entry.state, entry.rejectionReason)}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-900">
                              {entry.task || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-700 max-w-md truncate" title={entry.description || undefined}>
                              {entry.description || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            {entry.state === 'rejected' && entry.rejectionReason ? (
                              <div className="text-sm text-red-600 max-w-md truncate" title={entry.rejectionReason}>
                                {entry.rejectionReason}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          {!readOnly && onEditEntry && (
                            <td className="px-6 py-3 text-right">
                              {(entry.state === 'open' || entry.state === 'rejected') ? (
                                <button
                                  onClick={() => onEditEntry(entry)}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit entry"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Total Hours Row */}
                    {!isCollapsed && (() => {
                      const approvedHours = group.entries
                        .filter(e => e.state === 'approved')
                        .reduce((sum, e) => sum + e.hours, 0);
                      const submittedHours = group.entries
                        .filter(e => e.state === 'submitted')
                        .reduce((sum, e) => sum + e.hours, 0);
                      const rejectedHours = group.entries
                        .filter(e => e.state === 'rejected')
                        .reduce((sum, e) => sum + e.hours, 0);

                      return (
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <td colSpan={readOnly ? 2 : 3} className="px-6 py-3 text-left">
                            <span className="text-sm font-medium text-gray-700">
                              Total hours per day:
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2 text-sm">
                              <span className="font-bold text-gray-900">
                                {group.totalHours.toFixed(1)}
                              </span>
                              <span className="text-xs text-gray-500">
                                (
                                {approvedHours > 0 && (
                                  <span className="text-green-600 font-medium">{approvedHours.toFixed(1)}</span>
                                )}
                                {approvedHours > 0 && (submittedHours > 0 || rejectedHours > 0) && <span className="text-gray-400"> / </span>}
                                {submittedHours > 0 && (
                                  <span className="text-yellow-600 font-medium">{submittedHours.toFixed(1)}</span>
                                )}
                                {submittedHours > 0 && rejectedHours > 0 && <span className="text-gray-400"> / </span>}
                                {rejectedHours > 0 && (
                                  <span className="text-red-600 font-medium">{rejectedHours.toFixed(1)}</span>
                                )}
                                )
                              </span>
                            </div>
                          </td>
                          <td colSpan={!readOnly && onEditEntry ? 4 : 3}></td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })
            ) : (
              projectGroups.map((group) => {
                const isCollapsed = collapsedProjects.has(group.projectId);

                // MKS-192: the project checkbox acts on the same set as the
                // per-entry checkboxes — only submitted entries are selectable.
                const groupSelectableIds = group.entries
                  .filter((e) => selectableIds.has(e.id))
                  .map((e) => e.id);
                const groupSelectedCount = groupSelectableIds.filter((entryId) =>
                  selectedEntryIds.has(entryId)
                ).length;
                const groupAllSelected =
                  groupSelectableIds.length > 0 && groupSelectedCount === groupSelectableIds.length;
                const totalColumns = readOnly ? 6 : onEditEntry ? 8 : 7;

                return (
                  <React.Fragment key={group.projectId}>
                    {/* Project Header Row */}
                    <tr className="bg-gray-100 border-t border-b border-gray-200">
                      {showProjectSelect && (
                        <td className="px-4 py-3 w-10">
                          <ProjectSelectCheckbox
                            checked={groupAllSelected}
                            indeterminate={!groupAllSelected && groupSelectedCount > 0}
                            disabled={groupSelectableIds.length === 0}
                            label={`Select all submitted entries for ${group.projectFullName}`}
                            onChange={() => onToggleProject?.(groupSelectableIds)}
                          />
                        </td>
                      )}
                      <td
                        colSpan={showProjectSelect ? totalColumns - 1 : totalColumns}
                        className="px-4 py-3"
                      >
                        <button
                          onClick={() => toggleProjectCollapse(group.projectId)}
                          className="flex items-center gap-2 w-full text-left hover:text-blue-600 transition-colors"
                        >
                          {isCollapsed ? (
                            <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm text-gray-900">
                            {group.projectFullName}
                          </span>
                          <span className="text-xs text-gray-500 ml-auto">
                            ({group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}, {group.totalHours.toFixed(1)}h total)
                          </span>
                        </button>
                      </td>
                    </tr>

                    {/* Entry Rows */}
                    {!isCollapsed && group.entries.map((entry) => {
                      const selectable = isSelectable(entry);
                      const selected = selectedEntryIds.has(entry.id);

                      return (
                        <tr
                          key={entry.id}
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                            selected ? 'bg-blue-50' : ''
                          }`}
                        >
                          {!readOnly && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selected}
                                disabled={!selectable}
                                onChange={() => onToggleEntry(entry.id)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </td>
                          )}
                          <td className="px-6 py-3">
                            {/* Empty - project name is in header */}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(entry.date)}
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="text-sm font-medium text-gray-900">
                                {entry.hours.toFixed(1)}
                              </span>
                              {getStatusIcon(entry.state, entry.rejectionReason)}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-900">
                              {entry.task || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-700 max-w-md truncate" title={entry.description || undefined}>
                              {entry.description || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            {entry.state === 'rejected' && entry.rejectionReason ? (
                              <div className="text-sm text-red-600 max-w-md truncate" title={entry.rejectionReason}>
                                {entry.rejectionReason}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          {!readOnly && onEditEntry && (
                            <td className="px-6 py-3 text-right">
                              {(entry.state === 'open' || entry.state === 'rejected') ? (
                                <button
                                  onClick={() => onEditEntry(entry)}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit entry"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Total Hours Row */}
                    {!isCollapsed && (() => {
                      // Calculate hours by status
                      const approvedHours = group.entries
                        .filter(e => e.state === 'approved')
                        .reduce((sum, e) => sum + e.hours, 0);
                      const submittedHours = group.entries
                        .filter(e => e.state === 'submitted')
                        .reduce((sum, e) => sum + e.hours, 0);
                      const rejectedHours = group.entries
                        .filter(e => e.state === 'rejected')
                        .reduce((sum, e) => sum + e.hours, 0);

                      return (
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <td colSpan={readOnly ? 2 : 3} className="px-6 py-3 text-left">
                            <span className="text-sm font-medium text-gray-700">
                              Total hours per project:
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2 text-sm">
                              <span className="font-bold text-gray-900">
                                {group.totalHours.toFixed(1)}
                              </span>
                              <span className="text-xs text-gray-500">
                                (
                                {approvedHours > 0 && (
                                  <span className="text-green-600 font-medium">{approvedHours.toFixed(1)}</span>
                                )}
                                {approvedHours > 0 && (submittedHours > 0 || rejectedHours > 0) && <span className="text-gray-400"> / </span>}
                                {submittedHours > 0 && (
                                  <span className="text-yellow-600 font-medium">{submittedHours.toFixed(1)}</span>
                                )}
                                {submittedHours > 0 && rejectedHours > 0 && <span className="text-gray-400"> / </span>}
                                {rejectedHours > 0 && (
                                  <span className="text-red-600 font-medium">{rejectedHours.toFixed(1)}</span>
                                )}
                                )
                              </span>
                            </div>
                          </td>
                          <td colSpan={!readOnly && onEditEntry ? 4 : 3}></td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
